import tw from "tailwind-styled-components";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { IoIosArrowForward } from "react-icons/io";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    Filler
);


// const kpis = [
//     { label: "Rentable Beds", value: "198", color: "bg-blue-50 text-blue-600" },
//     { label: "Occupied Beds", value: "151", color: "bg-green-50 text-green-600" },
//     { label: "Occupancy Rate", value: "76.3%", color: "bg-indigo-50 text-indigo-600" },
//     { label: "Vacant Beds", value: "47", color: "bg-orange-50 text-orange-600" },
//     { label: "Moving Out", value: "8", color: "bg-red-50 text-red-600" },
// ];

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: "bottom",
            labels: {
                usePointStyle: true,
                boxWidth: 7,
                padding: 18,
                color: "#6b7280",
                font: { size: 11 },
            },
        },
    },
};

const axisOptions = {
    ...chartOptions,
    scales: {
        x: {
            grid: { display: false },
            ticks: { color: "#9ca3af", font: { size: 10 } },
            border: { display: false },
        },
        y: {
            beginAtZero: true,
            grid: { color: "#f3f4f6" },
            ticks: { color: "#9ca3af", font: { size: 10 }, precision: 0 },
            border: { display: false },
        },
    },
};

const occupancyStatusData = {
    labels: ["Occupied", "Vacant", "Under Notice", "Booked"],
    datasets: [
        {
            data: [151, 39, 8, 12],
            backgroundColor: ["#10b981", "#f59e0b", "#ef4444", "#6366f1"],
            borderWidth: 0,
            hoverOffset: 4,
        },
    ],
};

const vacancyAgeingData = {
    labels: ["0–7 days", "8–15 days", "16–30 days", "31+ days"],
    datasets: [
        {
            label: "Vacant Beds",
            data: [15, 12, 9, 11],
            backgroundColor: ["#93c5fd", "#60a5fa", "#3b82f6", "#ef4444"],
            borderRadius: 5,
            maxBarThickness: 42,
        },
    ],
};

const occupancyTrendData = {
    labels: ["Sep 26", "Oct 26"],
    datasets: [
        {
            label: "Occupied",
            data: [151, 151],
            backgroundColor: "#6385FF",
            borderRadius: {
                topLeft: 7,
                topRight: 7,
                bottomLeft: 0,
                bottomRight: 0,
            },
            borderSkipped: false,
            stack: "occupancy",
            maxBarThickness: 34,
        },
        {
            label: "Vacant",
            data: [47, 47],
            backgroundColor: "#FFBF55",
            borderRadius: {
                topLeft: 0,
                topRight: 0,
                bottomLeft: 7,
                bottomRight: 7,
            },
            borderSkipped: false,
            stack: "occupancy",
            maxBarThickness: 34,
        },
    ],
};
// ---- Occupancy Trend (API-driven) ----

const agreementExpiryData = {
    labels: ["Within 30 days", "31–60 days", "61–90 days"],
    datasets: [
        {
            label: "Agreements",
            data: [9, 7, 8],
            backgroundColor: ["#ef4444", "#6366f1", "#3b82f6"],
            borderRadius: 5,
            maxBarThickness: 48,
        },
    ],
};

const occupancyTargetData = {
    labels: ["May", "Jun", "Jul", "Aug", "Sep", "Oct"],
    datasets: [
        {
            label: "Actual Occupancy (%)",
            data: [67, 70, 73, 75, 74, 76.3],
            borderColor: "#4f6bed",
            backgroundColor: "#4f6bed",
            borderRadius: 5,
        },
        {
            label: "Target (%)",
            data: [85, 85, 85, 85, 85, 85],
            borderColor: "#f59e0b",
            backgroundColor: "#f59e0b",
            borderRadius: 5,
        },
    ],
};

const moveInOutData = {
    labels: ["May", "Jun", "Jul", "Aug", "Sep", "Oct"],
    datasets: [
        {
            label: "Move-ins",
            data: [18, 22, 19, 24, 17, 21],
            backgroundColor: "#10b981",
            borderRadius: 4,
        },
        {
            label: "Move-outs",
            data: [14, 15, 13, 21, 19, 16],
            backgroundColor: "#f87171",
            borderRadius: 4,
        },
    ],
};

const sharingTypeData = {
    labels: ["Single", "Double", "Triple", "4 Sharing"],
    datasets: [
        {
            label: "Occupied Beds",
            data: [28, 64, 42, 17],
            backgroundColor: "#10b981",
            borderRadius: 4,
        },
        {
            label: "Vacant Beds",
            data: [8, 18, 12, 9],
            backgroundColor: "#fbbf24",
            borderRadius: 4,
        },
    ],
};

const propertyOccupancy = [
    { name: "Sayba Residency C1402", occupied: 5, total: 9 },
    { name: "Mass Metropolis 602", occupied: 1, total: 5 },
    { name: "Dreams Complex 2D 403", occupied: 2, total: 6 },
    { name: "Sayba Residency C601", occupied: 3, total: 7 },
];

const upcomingVacancies = [
    { property: "Sayba Residency", room: "Room 102", date: "15 Oct 2026" },
    { property: "Mass Metropolis", room: "Room 204", date: "18 Oct 2026" },
    { property: "Dreams Complex", room: "Room 301", date: "22 Oct 2026" },
];

const GraphCard = ({ title, subtitle, children }) => (
    <Card>
        <CardHeader>
            <div>
                <CardTitle>{title}</CardTitle>
                {subtitle && <SubText className="mt-1">{subtitle}</SubText>}
            </div>
        </CardHeader>
        <CardBody>{children}</CardBody>
    </Card>
);

const OccupancyOverview = ({ data }) => {

    const summary = data?.bedCountSummary ?? {};
    const sharing = data?.bedCountByStausAndSharing ?? {};

    const totalBeds = Number(summary.total ?? 0);
    const occupiedBeds = Number(summary.occupied ?? 0);
    const vacantBeds = Number(summary.vacant ?? 0);
    const movingOut = Number(summary.movingOut ?? 0);

    const occupancyRate =
        totalBeds > 0 ? (occupiedBeds / totalBeds) * 100 : 0;

    const occupancyStatusData = {
        labels: ["Occupied", "Vacant", "Moving Out"],
        datasets: [
            {
                data: [occupiedBeds, vacantBeds, movingOut],
                backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
                borderWidth: 0,
                hoverOffset: 4,
            },
        ],
    };

    const vacancyAgeingData = {
        labels: Object.keys(data?.vacantBedAgeGraph ?? {}),
        datasets: [
            {
                label: "Vacant Beds",
                data: Object.values(data?.vacantBedAgeGraph ?? {}).map(Number),
                backgroundColor: ["#93c5fd", "#60a5fa", "#3b82f6", "#ef4444"],
                borderRadius: 5,
                maxBarThickness: 42,
            },
        ],
    };

    const agreementExpiryData = {
        labels: Object.keys(data?.agreementEndCountAgeGraph ?? {}),
        datasets: [
            {
                label: "Agreements",
                data: Object.values(data?.agreementEndCountAgeGraph ?? {}).map(Number),
                backgroundColor: ["#ef4444", "#6366f1", "#3b82f6"],
                borderRadius: 5,
                maxBarThickness: 48,
            },
        ],
    };

    const movement = data?.moveInOutTrend ?? [];

    const moveInOutData = {
        labels: movement.map(
            (item) =>
                `${new Date(item.year, item.month - 1).toLocaleString("en-IN", {
                    month: "short",
                })} ${String(item.year).slice(-2)}`
        ),
        datasets: [
            {
                label: "Move-ins",
                data: movement.map((item) => Number(item.moveInCount ?? 0)),
                backgroundColor: "#10b981",
                borderRadius: 4,
            },
            {
                label: "Move-outs",
                data: movement.map((item) => Number(item.movedOutCount ?? 0)),
                backgroundColor: "#f87171",
                borderRadius: 4,
            },
        ],
    };

    const sharingTypeData = {
        labels: ["Single", "Double", "Triple", "4+ Sharing"],
        datasets: [
            {
                label: "Occupied Beds",
                data: [
                    Number(sharing.SingleOccupied ?? 0),
                    Number(sharing.DoubleOccupied ?? 0),
                    Number(sharing.TripleOccupied ?? 0),
                    Number(sharing.TriplePlusOccupied ?? 0),
                ],
                backgroundColor: "#10b981",
                borderRadius: 4,
            },
            {
                label: "Vacant Beds",
                data: [
                    Number(sharing.SingleVacant ?? 0),
                    Number(sharing.DoubleVacant ?? 0),
                    Number(sharing.TripleVacant ?? 0),
                    Number(sharing.TriplePlusVacant ?? 0),
                ],
                backgroundColor: "#fbbf24",
                borderRadius: 4,
            },
        ],
    };

    const trend = data?.occupancyTrend ?? [];

    const formatMonthLabel = (month, year) => {
        const label = new Date(year, month - 1).toLocaleString("en-IN", {
            month: "short",
        });
        return `${label} ${String(year).slice(-2)}`;
    };

    const occupancyTrendData = {
        labels: trend.map((item) => formatMonthLabel(item.month, item.year)),
        datasets: [
            {
                label: "Occupied",
                data: trend.map((item) => Number(item.occupied ?? 0)),
                backgroundColor: "#6385FF",
                borderRadius: {
                    topLeft: 7,
                    topRight: 7,
                    bottomLeft: 0,
                    bottomRight: 0,
                },
                borderSkipped: false,
                stack: "occupancy",
                maxBarThickness: 34,
            },
            {
                label: "Vacant",
                data: trend.map((item) => Number(item.vacant ?? 0)),
                backgroundColor: "#FFBF55",
                borderRadius: {
                    topLeft: 0,
                    topRight: 0,
                    bottomLeft: 7,
                    bottomRight: 7,
                },
                borderSkipped: false,
                stack: "occupancy",
                maxBarThickness: 34,
            },
        ],
    };

    const target = data?.last12MonthOccupancyPercentage ?? [];

    const occupancyTargetData = {
        labels: target.map((item) => formatMonthLabel(item.month, item.year)),
        datasets: [
            {
                label: "Actual Occupancy (%)",
                data: target.map((item) => Number(item.occupancyRate ?? 0)),
                borderColor: "#4f6bed",
                backgroundColor: "#4f6bed",
                borderRadius: 5,
            },
            {
                label: "Target (%)",
                data: target.map(() => 85),
                // data: [85, 85, 85, 85, 85, 85],
                borderColor: "#f59e0b",
                backgroundColor: "#f59e0b",
                borderRadius: 5,
            },
        ],
    };

    return (
        <div className="space-y-4">
            {/* KPIs */}
            <OccupancyKPIs
                data={{
                    rentableBeds: totalBeds,
                    activeBeds: totalBeds,
                    occupiedBeds,
                    occupancyRate,
                    vacantBeds,
                    underNoticeBeds: movingOut,
                    moveIns: data?.moveInOutTrend?.at(-1)?.moveInCount ?? 0,
                    moveOuts: data?.moveInOutTrend?.at(-1)?.movedOutCount ?? 0,
                }}
            />

            {/* Two-column graphs */}
            <GraphGrid>
                <GraphCard
                    title="Occupancy Status"
                    subtitle="Distribution of beds across their current status"
                >
                    <div className="mx-auto h-[250px] max-w-[340px]">
                        <Doughnut
                            data={occupancyStatusData}
                            options={{
                                ...chartOptions,
                                cutout: "68%",
                            }}
                        />
                    </div>
                </GraphCard>

                <GraphCard
                    title="Vacancy Ageing"
                    subtitle="How long beds have remained vacant"
                >
                    <div className="h-[250px]">
                        <Bar data={vacancyAgeingData} options={axisOptions} />
                    </div>
                </GraphCard>

                <GraphCard
                    title="Occupancy Trend"
                    subtitle="Monthly occupied and vacant bed movement"
                >
                    <div className="h-[275px]">
                        <Bar
                            data={occupancyTrendData}
                            plugins={[occupancyValuePlugin]}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,

                                plugins: {
                                    legend: {
                                        display: true,
                                        position: "bottom",
                                        labels: {
                                            usePointStyle: true,
                                            pointStyle: "circle",
                                            boxWidth: 12,
                                            boxHeight: 12,
                                            padding: 18,
                                            color: "#6b7280",
                                            font: { size: 11 },
                                        },
                                    },
                                    tooltip: {
                                        mode: "index",
                                        intersect: false,
                                    },
                                },

                                scales: {
                                    x: {
                                        stacked: true,
                                        grid: { display: false },
                                        border: { display: false },
                                        ticks: {
                                            color: "#9ca3af",
                                            font: { size: 10 },
                                        },
                                    },
                                    y: {
                                        stacked: true,
                                        display: false,
                                        beginAtZero: true,
                                        grid: { display: false },
                                        border: { display: false },
                                    },
                                },
                            }}
                        />
                    </div>
                </GraphCard>

                <GraphCard
                    title="Agreements Ending Soon"
                    subtitle="Agreements expiring in the next 90 days"
                >
                    <div className="h-[280px]">
                        <Bar data={agreementExpiryData} options={axisOptions} />
                    </div>
                </GraphCard>

                {/* Property-wise occupancy */}
                {/* <GraphCard
                    title="Property-wise Occupancy"
                    subtitle="Occupied beds compared with total rentable beds"
                >
                    <div className="space-y-5">
                        {propertyOccupancy.map((item) => {
                            const percentage = Math.round(
                                (item.occupied / item.total) * 100
                            );

                            return (
                                <div key={item.name}>
                                    <div className="mb-2 flex items-center justify-between gap-3">
                                        <span className="truncate text-sm text-gray-700">
                                            {item.name}
                                        </span>
                                        <span className="whitespace-nowrap text-xs font-semibold text-gray-600">
                                            {item.occupied}/{item.total}
                                        </span>
                                    </div>

                                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                                        <div
                                            className="h-full rounded-full bg-emerald-500"
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                    <SubText className="mt-1">{percentage}% occupied</SubText>
                                </div>
                            );
                        })}
                    </div>
                </GraphCard> */}

                {/* Upcoming vacancy */}
                {/* <GraphCard
                    title="Upcoming Vacancy"
                    subtitle="Expected move-outs and upcoming bed availability"
                >
                    <div className="divide-y divide-gray-100">
                        {upcomingVacancies.map((item) => (
                            <div
                                key={`${item.property}-${item.room}`}
                                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-700">
                                        {item.property}
                                    </p>
                                    <SubText className="mt-1">{item.room}</SubText>
                                </div>
                                <span className="whitespace-nowrap rounded-md bg-orange-50 px-2 py-1 text-xs text-orange-700">
                                    {item.date}
                                </span>
                            </div>
                        ))}
                    </div>
                </GraphCard> */}

                <GraphCard
                    title="Occupancy vs Target"
                    subtitle="Compare actual occupancy with your target"
                >
                    <div className="h-[280px]">
                        <Line
                            data={occupancyTargetData}
                            options={{
                                ...axisOptions,
                                scales: {
                                    ...axisOptions.scales,
                                    y: {
                                        ...axisOptions.scales.y,
                                        min: 0,
                                        max: 100,
                                        ticks: {
                                            ...axisOptions.scales.y.ticks,
                                            callback: (value) => `${value}%`,
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                </GraphCard>

                <GraphCard
                    title="Move-in vs Move-out"
                    subtitle="Monthly tenant movement"
                >
                    <div className="h-[280px]">
                        <Bar
                            data={moveInOutData}
                            options={axisOptions}
                        />
                    </div>
                </GraphCard>

                <GraphCard
                    title="Sharing-Type Occupancy"
                    subtitle="Occupied and vacant beds by room sharing"
                >
                    <div className="h-[280px]">
                        <Bar
                            data={sharingTypeData}
                            options={axisOptions}
                        />
                    </div>
                </GraphCard>


            </GraphGrid>

            {/* Full-width revenue insight */}
            {/* <GraphCard
                title="Revenue Lost Due to Vacant Beds"
                subtitle="Illustrative monthly rent loss based on vacant inventory"
            >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="rounded-lg bg-red-50 p-4">
                        <SubText>Estimated Monthly Loss</SubText>
                        <p className="mt-2 text-2xl font-semibold text-red-600">
                            ₹32,060
                        </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                        <SubText>Vacant Beds</SubText>
                        <p className="mt-2 text-2xl font-semibold text-gray-800">47</p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                        <SubText>Average Rent per Bed</SubText>
                        <p className="mt-2 text-2xl font-semibold text-gray-800">
                            ₹4,500
                        </p>
                    </div>
                </div>
            </GraphCard> */}
        </div>
    );
};

function OccupancyKPIs({ data }) {
    const summary = {
        rentableBeds: data?.rentableBeds ?? 0,
        activeBeds: data?.activeBeds ?? 0,
        occupiedBeds: data?.occupiedBeds ?? 0,
        occupancyRate: data?.occupancyRate ?? 0,
        vacantBeds: data?.vacantBeds ?? 0,
        underNoticeBeds: data?.underNoticeBeds ?? 0,
        moveIns: data?.moveIns ?? 0,
        moveOuts: data?.moveOuts ?? 0,
        netChange: data?.netChange ?? 0,
    };

    const metrics = [
        {
            label: "Total Beds",
            value: summary.rentableBeds,
            color: "text-slate-800",
        },
        // {
        //     label: "Active Beds",
        //     value: summary.activeBeds,
        //     color: "text-blue-600",
        // },
        {
            label: "Occupied Beds",
            value: summary.occupiedBeds,
            //vacancyPercent: `${Number(summary.occupancyRate).toFixed(1)}%`,
            color: "text-emerald-600",
        },
        {
            label: "Occupancy Rate",
            value: `${Number(summary.occupancyRate).toFixed(1)}%`,
            color: "text-blue-600",
        },
        {
            label: "Vacant Beds",
            value: summary.vacantBeds,
            color: "text-amber-600",
        },
        {
            label: "Moving Out",
            value: summary.underNoticeBeds,
            color: "text-rose-600",
        },
        {
            label: "Move-ins",
            value: summary.moveIns,
            color: "text-emerald-600",
        },
        {
            label: "Move-outs",
            value: summary.moveOuts,
            color: "text-rose-600",
        },
        // {
        //     label: "Net Occupancy Change",
        //     value: summary.netChange > 0 ? `+${summary.netChange}` : summary.netChange,
        //     color: summary.netChange >= 0 ? "text-emerald-600" : "text-rose-600",
        // },
    ];

    return (
        <Container>
            <MetricsRow>
                <MetricsScroll>
                    {metrics.map((item) => (
                        <Metric key={item.label}>
                            <MetricLabel>{item.label}</MetricLabel>

                            <MetricValue className={item.color}>
                                {item.value}
                                {item.vacancyPercent !== undefined && (
                                    <VacancyPercent>
                                        ({item.vacancyPercent}%)
                                    </VacancyPercent>
                                )}
                                <Arrow />
                            </MetricValue>
                        </Metric>
                    ))}
                </MetricsScroll>
            </MetricsRow>
        </Container>
    );
}

const occupancyValuePlugin = {
    id: "occupancyValuePlugin",

    afterDatasetsDraw(chart) {
        const { ctx } = chart;

        ctx.save();
        ctx.font = "10px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#FFFFFF";

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);

            meta.data.forEach((bar, index) => {
                const value = dataset.data[index];

                if (value == null) return;

                const { x, y, base } = bar.getProps(
                    ["x", "y", "base"],
                    true
                );

                ctx.fillText(String(value), x, (y + base) / 2);
            });
        });

        ctx.restore();
    },
};

const Card = tw.div`min-w-0 rounded-lg bg-white shadow-sm`;
const CardHeader = tw.div`flex items-center justify-between border-b border-gray-100 px-4 py-3`;
const CardTitle = tw.h3`text-sm font-semibold text-gray-800`;
const CardBody = tw.div`p-4`;
const SubText = tw.p`text-xs text-gray-400`;
const KPIGrid = tw.div`grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5`;
const KPIValue = tw.p`text-xl font-semibold text-gray-800`;
const KPILabel = tw.p`mt-1 text-xs text-gray-500`;
const GraphGrid = tw.div`grid grid-cols-1 gap-4 xl:grid-cols-2 pb-8`;

const Container = tw.div`
  mt-2 md:mt-6
  bg-white rounded-lg shadow-sm
  px-4 py-4 md:px-6
`;

const MetricsScroll = tw.div`
  grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9
  gap-x-4 gap-y-4
  pb-1
`;

const Metric = tw.div`
  min-w-0
  cursor-default
  group
`;

const MetricLabel = tw.div`
  text-[9px] md:text-[11px]
  uppercase tracking-wide
  text-gray-500
  whitespace-nowrap
`;

const MetricValue = tw.div`
  flex items-center gap-1
  text-lg font-semibold
  transition-colors
`;

const Arrow = tw(IoIosArrowForward)`
  text-gray-400 text-xs
  opacity-0 group-hover:opacity-100
  transition-opacity
`;

const MetricsRow = tw.div`
  flex-1 min-w-0
`;

const VacancyPercent = tw.span`
  text-[10px] md:text-[11px]
  font-medium
  text-gray-400
  ml-0.5
`;
export default OccupancyOverview;